import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, CircleDashed, Loader2, PlugZap, Users } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { integracionesApi } from '../../utils/api';

/**
 * Configuración → Integraciones: la conexión de la empresa con los otros productos de la suite.
 * Hoy, Payroll 360: de ahí el presupuesto trae a la gente (plan de personal) y los departamentos.
 * La planilla va en la otra dirección (Payroll la publica en Finance con su propia credencial).
 */
const campo = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200 outline-none';

export default function Integraciones() {
  const { currentEmisor } = useAuthStore();
  const [con, setCon] = useState(null);
  const [f, setF] = useState({ url: '', inquilino: '', apiKey: '', apiSecret: '' });
  const [trabajando, setTrabajando] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await integracionesApi.payroll();
      const d = r.data.data;
      setCon(d);
      setF({ url: d.url || '', inquilino: d.inquilino || '', apiKey: d.apiKey || '', apiSecret: '' });
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo leer la conexión con Payroll'); }
  }, []);
  useEffect(() => { cargar(); }, [cargar, currentEmisor?.id]);

  const accion = async (nombre, fn, exito) => {
    setTrabajando(nombre);
    try { const r = await fn(); setCon(r.data.data); setF(x => ({ ...x, apiSecret: '' })); if (exito) toast.success(exito(r.data.data)); }
    catch (e) { toast.error(e.response?.data?.error || 'No se pudo completar'); }
    finally { setTrabajando(null); }
  };
  const guardar = (ev) => {
    ev.preventDefault();
    accion('guardar', () => integracionesApi.guardarPayroll({ ...f, apiSecret: f.apiSecret || undefined }), () => 'Conexión guardada. Pruébela para confirmar.');
  };
  const probar = () => accion('probar', () => integracionesApi.probarPayroll(), (d) => d.mensaje);
  const quitar = () => accion('quitar', () => integracionesApi.quitarPayroll(), () => 'Conexión quitada');

  const estado = !con?.configurada ? ['Sin conectar', 'bg-gray-100 text-gray-600', CircleDashed]
    : con.pruebaOk === true ? ['Conectado', 'bg-emerald-50 text-emerald-700', CheckCircle2]
      : con.pruebaOk === false ? ['La prueba falló', 'bg-red-50 text-red-700', XCircle]
        : ['Sin probar', 'bg-amber-50 text-amber-800', CircleDashed];
  const [rotulo, clase, Icono] = estado;

  return (
    <div className="space-y-6 w-full max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Integraciones de {currentEmisor?.nombre || 'la empresa'}</h2>
        <p className="text-sm text-gray-500 mt-1">La conexión de esta empresa con los otros productos de la suite.</p>
      </div>
      <section className="bg-white border border-gray-200 rounded-xl" data-prueba="integracion-payroll">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <PlugZap className="text-indigo-600" size={22} />
            <div>
              <h3 className="font-semibold text-gray-900">Payroll 360</h3>
              <p className="text-xs text-gray-500">El presupuesto trae de aquí a la gente (salario pactado, departamento y puesto) y los departamentos.</p>
            </div>
          </div>
          <span className={clsx('inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full', clase)} data-prueba="estado-payroll"><Icono size={14} /> {rotulo}</span>
        </div>
        <form onSubmit={guardar} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {con?.pruebaMensaje && <div className={clsx('md:col-span-2 text-sm rounded-lg px-3 py-2', con.pruebaOk ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800')} data-prueba="mensaje-payroll">{con.pruebaMensaje}{con.probadoAt ? ` · ${new Date(con.probadoAt).toLocaleString('es-CR')}` : ''}</div>}
          <div className="md:col-span-2"><label htmlFor="payroll-url" className="block text-sm font-medium text-gray-700 mb-1">Dirección de Payroll</label>
            <input id="payroll-url" className={campo} value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://suempresa.payroll360lite.com" /></div>
          <div><label htmlFor="payroll-inquilino" className="block text-sm font-medium text-gray-700 mb-1">Cuenta de Payroll</label>
            <input id="payroll-inquilino" className={campo} value={f.inquilino} onChange={(e) => setF({ ...f, inquilino: e.target.value })} placeholder="Solo si la dirección no la lleva" />
            <p className="text-xs text-gray-500 mt-1">Va en la dirección (suempresa.payroll360lite.com). Llénela solo si la dirección no la trae, como en una instalación local.</p></div>
          <div><label htmlFor="payroll-llave" className="block text-sm font-medium text-gray-700 mb-1">Llave de la credencial</label>
            <input id="payroll-llave" className={clsx(campo, 'font-mono')} value={f.apiKey} onChange={(e) => setF({ ...f, apiKey: e.target.value })} placeholder="p360_…" autoComplete="off" /></div>
          <div className="md:col-span-2"><label htmlFor="payroll-secreto" className="block text-sm font-medium text-gray-700 mb-1">Secreto de la credencial</label>
            <input id="payroll-secreto" type="password" className={clsx(campo, 'font-mono')} value={f.apiSecret} onChange={(e) => setF({ ...f, apiSecret: e.target.value })} autoComplete="new-password"
              placeholder={con?.configurada ? 'Guardado. Escriba uno nuevo solo para cambiarlo.' : 'Se muestra una sola vez al crear la credencial en Payroll'} />
            <p className="text-xs text-gray-500 mt-1">En Payroll › Configuración › Credenciales API, cree una credencial para esta empresa con permiso de lectura de Empleados, Departamentos y Puestos. El secreto se guarda cifrado.</p></div>
          <div className="md:col-span-2 flex flex-wrap items-center gap-2 pt-1">
            <button type="submit" disabled={!!trabajando} className="px-4 py-2 border-2 border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 text-sm font-medium disabled:opacity-50">{trabajando === 'guardar' ? <Loader2 size={16} className="animate-spin" /> : 'Guardar'}</button>
            {con?.configurada && <button type="button" onClick={probar} disabled={!!trabajando} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-prueba="probar-payroll">{trabajando === 'probar' && <Loader2 size={14} className="animate-spin" />} Probar conexión</button>}
            {con?.configurada && <button type="button" onClick={quitar} disabled={!!trabajando} className="ml-auto px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm disabled:opacity-50">Quitar conexión</button>}
          </div>
        </form>
      </section>
      {con?.configurada && con.pruebaOk && <JefesDeDepartamento />}
    </div>
  );
}

/**
 * Los departamentos de Payroll con su jefe (P6). El jefe se asigna en Unity (RRHH › Departamentos)
 * y Payroll lo replica; sin Unity, se asigna en Payroll. Es el responsable del presupuesto de su
 * departamento, y para eso necesita un usuario de Finance con acceso a esta empresa.
 */
function JefesDeDepartamento() {
  const [filas, setFilas] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    integracionesApi.jefesPayroll().then(r => setFilas(r.data.data)).catch(e => setError(e.response?.data?.error || 'No se pudo leer Payroll'));
  }, []);
  return (
    <section className="bg-white border border-gray-200 rounded-xl" data-prueba="jefes-payroll">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
        <Users className="text-indigo-600" size={20} />
        <div>
          <h3 className="font-semibold text-gray-900">Departamentos y jefes</h3>
          <p className="text-xs text-gray-500">De Payroll. El jefe se asigna en Unity (RRHH › Departamentos) o, sin Unity, en Payroll. Es quien arma el presupuesto de su departamento.</p>
        </div>
      </div>
      {error && <div className="m-5 text-sm bg-red-50 text-red-800 rounded-lg px-3 py-2">{error}</div>}
      {!filas && !error && <div className="m-5 flex items-center gap-2 text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Leyendo Payroll…</div>}
      {filas && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 text-[10.5px] font-semibold tracking-wider uppercase text-gray-500"><tr><th className="text-left px-5 py-2">Departamento</th><th className="text-left px-3 py-2">Jefe</th><th className="text-left px-3 py-2">Usuario de Finance</th><th className="text-left px-3 py-2">Se asigna en</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {filas.map(d => (
                <tr key={d.nombre}>
                  <td className="px-5 py-2 font-medium text-gray-900">{d.nombre}</td>
                  <td className="px-3 py-2">{d.jefe ? <><div className="text-gray-900">{d.jefe.nombre}</div><div className="text-xs text-gray-500">{d.jefe.email}</div></> : <span className="text-xs text-amber-700">sin jefe</span>}</td>
                  <td className="px-3 py-2">
                    {!d.jefe ? <span className="text-gray-400">–</span>
                      : d.usuario?.acceso && d.usuario.activo ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 size={14} /> {d.usuario.nombre || d.usuario.email}</span>
                        : <span className="text-xs text-amber-800">{d.usuario ? 'sin acceso a esta empresa' : 'sin usuario'} · <Link to="/configuracion/usuarios" className="text-indigo-600 hover:underline">invitar</Link></span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{d.origen === 'unity' ? 'Unity 360' : 'Payroll'}</td>
                </tr>
              ))}
              {filas.length === 0 && <tr><td colSpan={4} className="px-5 py-6 text-center text-gray-400">Payroll no tiene departamentos.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
